import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import apiService from '../services/api';
import { validateDemoForm, createResolver, HONEYPOT_FIELD } from '../shared/validation';

const defaultValues = { name: '', email: '', phone: '', message: '', [HONEYPOT_FIELD]: '' };
const resolver = createResolver(validateDemoForm);

const DemoRequestDialog = ({ product, open, onOpenChange }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver,
    defaultValues,
    mode: 'onTouched'
  });

  // Start clean each time the dialog opens for a product
  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setIsSubmitted(false);
      setSubmitError('');
    }
  }, [open, product?.id, reset]);

  // react-hook-form blocks re-entry while isSubmitting, so a double click
  // cannot produce a second request.
  const onSubmit = async (values) => {
    setSubmitError('');

    try {
      const response = await apiService.submitDemoRequest({
        ...values,
        productName: product?.name
      });

      setIsSubmitted(true);
      toast.success(response.message);
    } catch (error) {
      setSubmitError(error.message);
      toast.error(error.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(e) => isSubmitting && e.preventDefault()}
        onEscapeKeyDown={(e) => isSubmitting && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Request a Demo</DialogTitle>
          <DialogDescription>
            {isSubmitted
              ? 'Your request is on its way.'
              : <>Tell us a little about yourself and we'll set up a walkthrough of{' '}
                  <span className="font-normal text-gray-900">{product?.name}</span>.</>}
          </DialogDescription>
        </DialogHeader>

        {isSubmitted ? (
          <div className="py-6 text-center">
            <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-gray-900" />
            </div>
            <p className="text-gray-600 leading-relaxed">
              Thank you! We've received your demo request for{' '}
              <span className="text-gray-900">{product?.name}</span> and will get back to you soon.
            </p>
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              className="mt-6 bg-black text-white hover:bg-gray-800 transition-colors duration-200"
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            {/* Product context, also sent with the submission */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">Product</p>
              <p className="text-sm font-normal text-gray-900 mt-1">{product?.name}</p>
            </div>

            {/* Honeypot: hidden from people, irresistible to bots */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="hidden"
              {...register(HONEYPOT_FIELD)}
            />

            <div>
              <Label htmlFor="demo-name">Name</Label>
              <Input
                id="demo-name"
                type="text"
                disabled={isSubmitting}
                className="mt-2"
                placeholder="Your name"
                aria-invalid={Boolean(errors.name)}
                {...register('name')}
              />
              {errors.name && <p className="mt-1.5 text-sm text-red-600">{errors.name.message}</p>}
            </div>

            <div>
              <Label htmlFor="demo-email">Email</Label>
              <Input
                id="demo-email"
                type="email"
                disabled={isSubmitting}
                className="mt-2"
                placeholder="your.email@example.com"
                aria-invalid={Boolean(errors.email)}
                {...register('email')}
              />
              {errors.email && <p className="mt-1.5 text-sm text-red-600">{errors.email.message}</p>}
            </div>

            <div>
              <Label htmlFor="demo-phone">Phone Number</Label>
              <Input
                id="demo-phone"
                type="tel"
                disabled={isSubmitting}
                className="mt-2"
                placeholder="+91 90000 00000"
                aria-invalid={Boolean(errors.phone)}
                {...register('phone')}
              />
              {errors.phone && <p className="mt-1.5 text-sm text-red-600">{errors.phone.message}</p>}
            </div>

            <div>
              <Label htmlFor="demo-message">
                Message <span className="text-gray-400 font-normal">(optional)</span>
              </Label>
              <Textarea
                id="demo-message"
                disabled={isSubmitting}
                className="mt-2 min-h-[110px]"
                placeholder="Anything specific you'd like us to cover?"
                {...register('message')}
              />
              {errors.message && <p className="mt-1.5 text-sm text-red-600">{errors.message.message}</p>}
            </div>

            {submitError && (
              <p className="text-sm text-red-600" role="alert">{submitError}</p>
            )}

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
                className="border-gray-200 text-gray-900 hover:bg-gray-100 transition-colors duration-200"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-black text-white hover:bg-gray-800 transition-colors duration-200"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DemoRequestDialog;
